import { useState, useMemo, useEffect, useRef } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useAtom } from "jotai"
import DOMPurify from "dompurify"
import api from "../services/api"
import { userAtom } from "../state"
import { SUPPORT_EMAIL } from "../constants/textConstants"
import { FAQ_CATEGORIES } from "../constants/faqConstants"

const FAQ = () => {
  const navigate = useNavigate()
  const [user] = useAtom(userAtom)
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get("q") ?? ""
  const activeCat = searchParams.get("cat") ?? "all"
  const openSlugs = new Set((searchParams.get("open") ?? "").split(",").filter(Boolean))
  const [faqs, setFaqs] = useState([])
  const [loading, setLoading] = useState(true)
  const [copiedSlug, setCopiedSlug] = useState(null)
  const autoScrolled = useRef(false)

  useEffect(() => {
    if (window.location.hash) {
      const slug = window.location.hash.replace("#", "")
      if (slug) {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev)
          next.set("open", slug)
          return next
        }, { replace: true })
      }
    }
  }, [])

  useEffect(() => {
    api.getFAQs()
      .then(res => {
        if (res.data.length === 0) { navigate("/", { replace: true }); return }
        setFaqs(res.data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!loading && faqs.length && !autoScrolled.current) {
      const opens = (searchParams.get("open") ?? "").split(",").filter(Boolean)
      const first = opens.find(s => faqs.some(f => f.slug === s))
      if (first) {
        autoScrolled.current = true
        setTimeout(() => {
          document.getElementById("faq-" + first)?.scrollIntoView({ behavior: "smooth", block: "start" })
        }, 100)
      }
    }
  }, [loading, faqs, searchParams])

  const counts = useMemo(() => {
    const c = { all: faqs.length }
    FAQ_CATEGORIES.forEach(cat => { c[cat.id] = faqs.filter(f => f.category === cat.id).length })
    return c
  }, [faqs])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return faqs.filter(f => {
      if (activeCat !== "all" && f.category !== activeCat) return false
      if (!q) return true
      const hay = [f.question, f.answer, ...(f.keywords || [])].join(" ").toLowerCase()
      return hay.includes(q)
    })
  }, [faqs, query, activeCat])

  const featured = useMemo(() =>
    faqs.filter(f => f.featured).sort((a, b) => (a.pinned_order ?? 999) - (b.pinned_order ?? 999)).slice(0, 3)
  , [faqs])

  const showFeatured = !query && activeCat === "all" && featured.length > 0

  const updateQuery = (val) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      val ? next.set("q", val) : next.delete("q")
      return next
    }, { replace: true })
  }

  const updateCat = (val) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      val === "all" ? next.delete("cat") : next.set("cat", val)
      return next
    }, { replace: true })
  }

  const toggle = (slug) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      const current = new Set((next.get("open") ?? "").split(",").filter(Boolean))
      current.has(slug) ? current.delete(slug) : current.add(slug)
      current.size ? next.set("open", [...current].join(",")) : next.delete("open")
      return next
    }, { replace: true })
  }

  const copyLink = (slug) => {
    const url = window.location.origin + "/faq?open=" + slug
    navigator.clipboard?.writeText(url)
    setCopiedSlug(slug)
    setTimeout(() => setCopiedSlug(null), 1600)
  }

  const openFeatured = (slug) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      const current = new Set((next.get("open") ?? "").split(",").filter(Boolean))
      current.add(slug)
      next.set("open", [...current].join(","))
      next.delete("q")
      next.delete("cat")
      return next
    }, { replace: false })
    // Delay enough for React to re-render the now-open accordion before scrolling
    setTimeout(() => {
      document.getElementById("faq-" + slug)?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 150)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <i className="fa fa-spinner fa-spin text-brand text-2xl"></i>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-background border-b border-gray-200 flex items-center justify-between px-8 py-4">
        <Link to="/">
          <img src="/logo.png" alt="Encircled" className="h-9" />
        </Link>
        {!user && (
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-semibold text-gray-600 hover:text-brand transition-colors">
              Login
            </Link>
            <Link
              to="/register"
              className="bg-brand text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-lg hover:bg-brand/90 transition-colors"
            >
              Get Started
            </Link>
          </div>
        )}
      </header>

      {/* Hero */}
      <div className="text-center px-6 pt-16 pb-10">
        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Help Center</p>
        <h1 className="text-4xl md:text-5xl font-bold text-brand mb-3 leading-tight">How can we help?</h1>
        <p className="text-base md:text-lg text-gray-500 max-w-xl mx-auto mb-9 leading-relaxed">
          Answers for youth, mentors, guides, and parents — from getting started to keeping your plan private.
        </p>
        <div className="max-w-2xl mx-auto relative">
          <i className="fa-solid fa-magnifying-glass absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none"></i>
          <input
            autoFocus
            value={query}
            onChange={e => updateQuery(e.target.value)}
            placeholder="Search questions, keywords, or topics…"
            className="w-full border border-gray-200 bg-white rounded-full py-4 pl-14 pr-12 text-base outline-none focus:ring-2 focus:ring-brand/30 shadow-sm"
          />
          {query && (
            <button onClick={() => updateQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-200">
              <i className="fa-solid fa-xmark text-sm"></i>
            </button>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pb-24">
        {/* Category chips */}
        <div className="flex flex-wrap gap-2 justify-center pb-8">
          <CategoryChip id="all" label="All" count={counts.all} active={activeCat === "all"} onClick={() => updateCat("all")} />
          {FAQ_CATEGORIES.map(cat => (
            <CategoryChip
              key={cat.id}
              id={cat.id}
              label={cat.label}
              count={counts[cat.id]}
              active={activeCat === cat.id}
              onClick={() => updateCat(cat.id)}
            />
          ))}
        </div>

        {/* Featured row */}
        {showFeatured && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <i className="fa-solid fa-star text-brand text-xs"></i>
              <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Most asked</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {featured.map(f => {
                const cat = FAQ_CATEGORIES.find(c => c.id === f.category)
                return (
                  <button
                    key={f.slug}
                    onClick={() => openFeatured(f.slug)}
                    className="text-left bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md hover:border-gray-200 transition-all flex flex-col justify-between gap-3 min-h-[90px]"
                  >
                    <p className="text-sm font-bold text-gray-900 leading-snug">{f.question}</p>
                    <div className="flex items-center justify-between">
                      {cat && (
                        <span className={`flex items-center gap-1.5 text-xs font-semibold ${cat.color}`}>
                          <span className={`w-2 h-2 rounded-full ${cat.dot}`}></span>
                          {cat.label}
                        </span>
                      )}
                      <i className="fa-solid fa-arrow-right text-gray-300 text-xs"></i>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Section heading */}
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">
            {query
              ? "Search results"
              : activeCat === "all"
                ? "All questions"
                : FAQ_CATEGORIES.find(c => c.id === activeCat)?.label}
          </h2>
          <span className="text-xs text-gray-400 font-medium">
            {filtered.length} {filtered.length === 1 ? "question" : "questions"}
          </span>
        </div>

        {/* Accordion list */}
        {filtered.length === 0
          ? <NoResults query={query} activeCat={activeCat} />
          : (
            <div className="flex flex-col gap-2.5">
              {filtered.map(f => (
                <AccordionItem
                  key={f.slug}
                  faq={f}
                  open={openSlugs.has(f.slug)}
                  onToggle={() => toggle(f.slug)}
                  copied={copiedSlug === f.slug}
                  onCopy={() => copyLink(f.slug)}
                  query={query}
                />
              ))}
            </div>
          )
        }

        {/* Still need help */}
        <div className="mt-14 bg-white border border-gray-100 rounded-2xl p-8 text-center shadow-sm">
          <h3 className="text-xl font-bold text-brandSecondary mb-2">Still need help?</h3>
          <p className="text-gray-500 text-sm leading-relaxed mb-5 max-w-sm mx-auto">
            We read every email. A real person will get back to you, usually within a day.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 bg-brand text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-lg hover:bg-brand/90 transition-colors"
          >
            <i className="fa-regular fa-envelope"></i>
            Email Support
          </a>
        </div>
      </div>
    </div>
  )
}

function CategoryChip({ id, label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 border rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${
        active
          ? "bg-brand border-brand text-white"
          : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
      }`}
    >
      {label}
      <span className="opacity-70 font-normal">{count}</span>
    </button>
  )
}

function AccordionItem({ faq, open, onToggle, copied, onCopy, query }) {
  const cat = FAQ_CATEGORIES.find(c => c.id === faq.category)
  const date = faq.updated_at
    ? new Date(faq.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null

  return (
    <div
      id={"faq-" + faq.slug}
      className={`bg-white rounded-xl border transition-colors ${open ? "border-gray-200" : "border-gray-100"} overflow-hidden`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 text-left"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-900 leading-snug">
            <Highlight text={faq.question} query={query} />
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {cat && (
            <span className={`hidden sm:flex items-center gap-1.5 text-xs font-semibold ${cat.color}`}>
              <span className={`w-2 h-2 rounded-full ${cat.dot}`}></span>
              {cat.label}
            </span>
          )}
          <i className={`fa-solid ${open ? "fa-chevron-up" : "fa-chevron-down"} text-gray-400 text-xs`}></i>
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <div
            className="faq-answer text-sm text-gray-700 leading-relaxed pt-4"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(faq.answer) }}
          />
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-dashed border-gray-100">
            <div className="flex items-center gap-4 text-xs text-gray-400">
              {date && <span><i className="fa-regular fa-clock mr-1.5"></i>Updated {date}</span>}
              {(faq.audiences || []).length > 0 && (
                <span>
                  <i className="fa-regular fa-user mr-1.5"></i>
                  For {(faq.audiences).map(a => a.charAt(0).toUpperCase() + a.slice(1)).join(", ")}
                </span>
              )}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onCopy() }}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                copied
                  ? "bg-green-50 border-green-500 text-green-700"
                  : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
              }`}
            >
              <i className={`fa-solid ${copied ? "fa-check" : "fa-link"} text-[10px]`}></i>
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Highlight({ text, query }) {
  if (!query?.trim()) return text
  const q = query.trim()
  const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"))
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase()
          ? <mark key={i} className="bg-brand/20 text-inherit px-0.5 rounded">{p}</mark>
          : <span key={i}>{p}</span>
      )}
    </>
  )
}

function NoResults({ query, activeCat }) {
  const isSearch = !!query.trim()
  const catLabel = FAQ_CATEGORIES.find(c => c.id === activeCat)?.label

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-12 text-center">
      <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <i className={`text-2xl text-gray-400 ${isSearch ? "fa-solid fa-magnifying-glass" : "fa-regular fa-folder-open"}`}></i>
      </div>
      {isSearch ? (
        <>
          <h3 className="text-lg font-bold text-gray-900 mb-1">No matches for "{query}"</h3>
          <p className="text-sm text-gray-500 mb-5">We couldn't find an answer for that. We'd love to help directly.</p>
        </>
      ) : (
        <>
          <h3 className="text-lg font-bold text-gray-900 mb-1">
            No {catLabel ? `${catLabel} ` : ""}FAQs yet
          </h3>
          <p className="text-sm text-gray-500 mb-5">Check back soon, or reach out if you have a question.</p>
        </>
      )}
      <a
        href={`mailto:${SUPPORT_EMAIL}?subject=Question about Encircled`}
        className="inline-flex items-center gap-2 bg-brandSecondary text-white text-sm font-semibold px-5 py-2.5 rounded-full"
      >
        <i className="fa-regular fa-envelope"></i>
        Email {SUPPORT_EMAIL}
      </a>
    </div>
  )
}

export default FAQ
